<template>
  <div class="filters-container" :class="loading ? 'disabled' : ''">
    <div
      class="d-flex flex-row justify-content-between align-items-center bg-light-gray min-width-fit-content"
    >
      <div class="d-flex flex-wrap align-items-center">
        <b-form-input
          class="auth-input search-input border-color-light-gray"
          v-model="searchPhrase"
          placeholder="Search patient..."
          :readonly="loading"
        />
        <span class="ml-4 mr-3" style="font-size: 14px;">Active filters:</span>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pS, pSIndex) in subscriberType"
          :key="pS.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Subscriber type:
              <span class="color-filter">{{ pS.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('subscriberType', pSIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pS, pSIndex) in insurance"
          :key="pS.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Insurance Company:
              <span class="color-filter">{{ pS.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('insurance', pSIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pS, pSIndex) in location"
          :key="pS.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Location: <span class="color-filter">{{ pS.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('location', pSIndex)"
            ></i>
          </div>
        </div>
        <div
          v-if="startDateFrom || startDateTo"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Start date:
              <span class="color-filter">{{
                startDateFrom || startDateTo
                  ? `(${
                      startDateFrom
                        ? "From: " + formatDateOnly(startDateFrom)
                        : ""
                    }${startDateFrom && startDateTo ? " - " : ""}${
                      startDateTo ? "To: " + formatDateOnly(startDateTo) : ""
                    })`
                  : ""
              }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeStartDateFilter()"
            ></i>
          </div>
        </div>
        <div
          v-if="endDateFrom || endDateTo"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >End date:
              <span class="color-filter"
                >{{
                  endDateFrom || endDateTo
                    ? `(${
                        endDateFrom
                          ? "From: " + formatDateOnly(endDateFrom)
                          : ""
                      }${endDateFrom && endDateTo ? " - " : ""}${
                        endDateTo ? "To: " + formatDateOnly(endDateTo) : ""
                      })`
                    : ""
                }}
              </span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeEndDateFilter()"
            ></i>
          </div>
        </div>
        <span
          v-if="hasFilters"
          class="clear-all-filters text-danger ml-2"
          style="font-size: 14px;"
          @click="clearAllFilters"
        >
          Clear all filters
        </span>
      </div>
      <div class="d-flex align-self-stretch align-items-center">
        <div class="align-self-stretch" style="padding: 9px 0px;">
          <div class="vertical-separator border-med-light-gray"></div>
        </div>
        <b-dropdown
          text="Filters"
          ref="filterDropdown"
          class="container-main bg-light-gray"
          toggle-class="d-flex flex-row justify-content-between align-items-lg-center bg-transparent border-0 color-custom-dark-grey dropdown-menu-label"
          menu-class="container-filter-main-menu"
          toggle-tag="div"
          boundary="window"
          right
        >
          <template #button-content>
            <div class="d-flex align-items-center mr-2">
              <i
                class="fas fa-filter text-primary mr-2"
                style="font-size: 15px;"
                aria-hidden="true"
              ></i>
              <span style="font-size: 14px;">Filters</span>
            </div>
          </template>
          <b-dropdown-form class="px-2">
            <b-form-group label="Filter by:">
              <b-form-select
                v-model="selectedFilter"
                :options="filterOptions"
              />
            </b-form-group>

            <multiple-select-filter
              v-if="selectedFilter == 'subscriberType'"
              label="Subscriber Type"
              :options="subscriberTypes"
              v-model="subscriberType"
              :width="'250px'"
              :showSearch="false"
              :key="'subscriberType'"
            />
            <multiple-select-filter
              v-if="selectedFilter == 'location'"
              label="Location"
              :options="locations"
              v-model="location"
              :width="'250px'"
              :showSearch="false"
              :key="'location'"
            />
            <v-select
              v-if="selectedFilter == 'insurance'"
              :class="[
                'insurance-company-picker',
                insurances.length == 0 ? 'hide-options' : ''
              ]"
              placeholder="Search for insurance companies"
              v-model="insurance"
              :options="insurances"
              @search="fetchInsuranceCompanies"
              :multiple="true"
            >
              <template #no-options="{ search }">
                {{
                  search && search.length > 3
                    ? "Sorry, no matching options"
                    : ""
                }}
              </template>
              <template #open-indicator>
                <span></span>
              </template>
            </v-select>
            <date-range-filter
              v-if="selectedFilter == 'startDate'"
              v-model="filterStartDateRange"
              :filterWidth="'250px'"
              label="Start Date"
              :key="'startDate'"
            />
            <date-range-filter
              v-if="selectedFilter == 'endDate'"
              v-model="filterEndDateRange"
              :filterWidth="'250px'"
              label="End Date"
              :key="'endDate'"
            />
          </b-dropdown-form>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
import insuranceCompanyServer from "../../server/insuranceCompany";
import { mapState } from "vuex";

export default {
  name: "EligibilityIndexFilters",
  props: ["value", "hasFilters", "loading"],
  data() {
    return {
      selectedFilter: "subscriberType",
      insurances: [],
      orderOptions: [
        {
          value: "name",
          text: "Name"
        },
        {
          value: "subscriberType",
          text: "Subscriber Type"
        },
        {
          value: "location",
          text: "Location"
        },
        {
          value: "insurance",
          text: "Insurance"
        },
        {
          value: "startDate",
          text: "Start date"
        },
        {
          value: "endDate",
          text: "End date"
        }
      ],
      filterOptions: [
        {
          value: "subscriberType",
          text: "Subscriber Type"
        },
        {
          value: "location",
          text: "Location"
        },
        {
          value: "insurance",
          text: "Insurance"
        },
        {
          value: "startDate",
          text: "Start date"
        },
        {
          value: "endDate",
          text: "End date"
        }
      ],
      subscriberTypes: [
        {
          value: "Prospect",
          label: "Prospect"
        },
        {
          value: "Patient",
          label: "Patient"
        }
      ],
      searchTimeout: null
    };
  },
  methods: {
    removeIndexFromFilter(filterName, index) {
      const filter = [...this.value[filterName]];
      filter.splice(index, 1);
      this.$emit("input", { ...this.value, [filterName]: filter });
    },
    removeStartDateFilter() {
      this.$emit("input", {
        ...this.value,
        startDateFrom: null,
        startDateTo: null
      });
    },
    removeEndDateFilter() {
      this.$emit("input", {
        ...this.value,
        endDateFrom: null,
        endDateTo: null
      });
    },
    clearAllFilters() {
      this.$emit("input", {
        ...this.value,
        searchPhrase: null,
        subscriberType: [],
        location: [],
        insurance: [],
        isProspect: false,
        startDateFrom: null,
        startDateTo: null,
        endDateFrom: null,
        endDateTo: null
      });
    },
    async fetchInsuranceCompanies(search, loading) {
      if (this.lastTimeoutInsurance) {
        clearTimeout(this.lastTimeoutInsurance);
        this.lastTimeoutInsurance = null;
      }
      if (search && search.trim().length >= 3) {
        this.insurances = [];
      }

      this.lastTimeoutInsurance = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const insurances = await insuranceCompanyServer.query(
            { searchPhrase: search, page: 0, pageSize: 1000000 },
            { "Hide-Loading": true }
          );
          this.insurances = insurances.records.map(insuranceCompany => ({
            value: insuranceCompany.id,
            label: insuranceCompany.name,
            data: insuranceCompany
          }));
        } else this.insurances = [];
        loading(false);
      }, 1000);
    }
  },
  computed: {
    ...mapState("client", {
      locations: state =>
        state.locations.map(loc => ({
          value: loc.id,
          label: loc.name
        }))
    }),
    searchPhrase: {
      get() {
        return this.value.searchPhrase;
      },
      set(newValue) {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
          this.searchTimeout = null;
        }
        this.searchTimeout = setTimeout(() => {
          this.$emit("input", { ...this.value, searchPhrase: newValue });
        }, 1000);
      }
    },
    subscriberType: {
      get() {
        return this.value.subscriberType;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, subscriberType: newValue });
      }
    },
    location: {
      get() {
        return this.value.location;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, location: newValue });
      }
    },
    insurance: {
      get() {
        return this.value.insurance;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, insurance: newValue });
      }
    },
    startDateFrom: {
      get() {
        return this.value.startDateFrom;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, startDateFrom: newValue });
      }
    },
    startDateTo: {
      get() {
        return this.value.startDateTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, startDateTo: newValue });
      }
    },
    filterStartDateRange: {
      get() {
        return {
          dateFrom: this.value.startDateFrom,
          dateTo: this.value.startDateTo
        };
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          startDateFrom: newValue.dateFrom,
          startDateTo: newValue.dateTo
        });
      }
    },
    endDateFrom: {
      get() {
        return this.value.endDateFrom;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, endDateFrom: newValue });
      }
    },
    endDateTo: {
      get() {
        return this.value.endDateTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, endDateTo: newValue });
      }
    },
    filterEndDateRange: {
      get() {
        return {
          dateFrom: this.value.endDateFrom,
          dateTo: this.value.endDateTo
        };
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          endDateFrom: newValue.dateFrom,
          endDateTo: newValue.dateTo
        });
      }
    },
    orderBy: {
      get() {
        return this.value.orderBy;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, orderBy: newValue });
      }
    },
    orderByText() {
      return this.orderOptions.find(opt => opt.value == this.orderBy).text;
    },
    orderByDescending: {
      get() {
        return this.value.orderByDescending;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, orderByDescending: newValue });
      }
    },
    sortingOrderClass() {
      return this.value.orderByDescending
        ? "fas fa-sort-amount-down"
        : "fas fa-sort-amount-up";
    }
  },
  watch: {
    selectedFilter: function() {
      this.$refs.filterDropdown.updatePopper();
    }
  }
};
</script>

<style scoped>
.filters-container {
  overflow-x: auto;
}
.filters-container >>> .container-filter-main-menu {
  min-width: 250px;
  left: -17px;
}
.filter-value {
  min-width: 100px;
  height: 29px;
  color: #a6a6a6;
  font-size: 14px;
  padding-left: 10px;
}
.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  width: 320px;
}
.color-filter {
  color: #909db8;
}
.clear-all-filters:hover {
  cursor: pointer;
}
.vertical-separator {
  width: 1px;
  border-left: 1px solid;
  height: 100%;
}
div.disabled {
  pointer-events: none;
  opacity: 0.5;
  background: #ccc;
}
.sorting-order-btn {
  background-color: white;
  color: #707070;
  border: 1px solid #dcdcdc;
  border-radius: 3px;
  padding: 1px 4px;
}

.filters-container >>> .v-select.vs--disabled .vs__dropdown-toggle {
  background-color: #f8f8f8 !important;
}

.filters-container >>> .insurance-company-picker .vs__search {
  min-width: 100px;
}

.filters-container
  >>> .insurance-company-picker
  .vs--open
  .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}

.filters-container
  >>> .insurance-company-picker.hide-options
  .vs__dropdown-menu {
  display: none;
}
</style>
